import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessagesSquare, ChevronRight, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { ReplyThreadDialog, isReplyRead } from './ReplyThreadDialog';
import { fetchOwnerReplyFeed } from '@/lib/replyFeed';

interface ConversationSummary {
  announcement_id: string;
  recipient_name: string;
  public_slug: string | null;
  reply_count: number;
  unread_count: number;
  latest_reply_at: string;
  latest_preview: string;
  has_thread: boolean;
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function ConversationsPanel() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openName, setOpenName] = useState<string>('');
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      setIsLoading(true);
      try {
        const feed = await fetchOwnerReplyFeed(user.id);
        const grouped = new Map<string, typeof feed>();
        feed.forEach((item) => {
          const group = grouped.get(item.announcement_id) || [];
          group.push(item);
          grouped.set(item.announcement_id, group);
        });

        const summaries: ConversationSummary[] = Array.from(grouped.entries()).map(([announcementId, items]) => {
          const sorted = [...items].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          const latest = sorted[0];
          return {
            announcement_id: announcementId,
            recipient_name: latest.recipient_name,
            public_slug: latest.public_slug,
            reply_count: sorted.length,
            unread_count: sorted.filter((i) => !isReplyRead(i.id)).length,
            latest_reply_at: latest.created_at,
            latest_preview: latest.text || '',
            has_thread: sorted.some((i) => i.source === 'thread_messages'),
          };
        }).sort((a, b) => (b.latest_reply_at || '').localeCompare(a.latest_reply_at || ''));

        setConversations(summaries);
      } catch (err) {
        console.error('Failed to load conversations:', err);
        setConversations([]);
      }
      setIsLoading(false);
    };

    load();
  }, [user, refreshTick]);

  if (isLoading || conversations.length === 0) return null;

  return (
    <Card className="border-0 rounded-2xl overflow-hidden shadow-soft bg-gradient-to-br from-card via-card to-primary/[0.04]">
      <div className="h-1 bg-gradient-to-r from-primary/40 via-primary/60 to-primary/40" />
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-primary/15">
            <MessagesSquare className="h-4 w-4 text-primary" />
          </div>
          <span>Active Conversations</span>
          <Badge variant="secondary" className="ml-auto text-xs rounded-md">
            {conversations.length}
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground pl-9 -mt-1">
          Your ongoing emotional threads
        </p>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {conversations.slice(0, 5).map((conv, i) => (
          <motion.button
            key={conv.announcement_id}
            type="button"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            onClick={() => {
              setOpenId(conv.announcement_id);
              setOpenName(conv.recipient_name);
            }}
            className="w-full text-left p-3.5 rounded-xl bg-background/70 hover:bg-background active:bg-muted/50 transition-all border border-border/60 hover:border-primary/30 group"
          >
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shrink-0 text-sm font-semibold text-primary">
                {conv.recipient_name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {conv.recipient_name}
                  </p>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {relativeTime(conv.latest_reply_at)}
                  </span>
                </div>
                {conv.latest_preview && (
                  <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5 italic">
                    "{conv.latest_preview}"
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] text-muted-foreground/70">
                    {conv.reply_count} {conv.reply_count === 1 ? 'message' : 'messages'}
                  </span>
                  {conv.unread_count > 0 && (
                    <Badge className="text-[10px] h-4 px-1.5 rounded-md bg-primary/90">
                      {conv.unread_count} unread
                    </Badge>
                  )}
                  <span className="ml-auto text-[11px] text-primary/80 font-medium flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    Continue <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary self-center shrink-0 transition-colors" />
            </div>
          </motion.button>
        ))}
      </CardContent>

      <ReplyThreadDialog
        open={!!openId}
        onOpenChange={(o) => {
          if (!o) {
            setOpenId(null);
            setRefreshTick((t) => t + 1);
          }
        }}
        announcementId={openId}
        recipientName={openName}
      />
    </Card>
  );
}
